import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTimes, faArrowUpFromBracket, faFolder, faFile, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { Button, FormControl, InputLabel, Select, MenuItem, Checkbox } from '@mui/material';
import { useTranslation } from 'react-i18next';
import KnowledgeLimitInfoModal from './knowledgeLimitInfoModal.js';
import ConfirmImportModal from './ConfirmImportModal.js';
import { resolveSelection } from '../utils/resolveSelection.js';
import './OneDrivePickerModal.css';

import { splitTooLarge, describeTooLarge } from '../utils/knowledgeLimits.js';
import ManageConnectionsModal from './ManageConnectionsModal.js';
const API_URL = process.env.REACT_APP_API_BASE_URL;

// OneDrive Logo Component
const OneDriveLogo = ({ size = 24, style = {} }) => (
  <img src="/onedrive.png" alt="OneDrive" style={{ height: size, width: 'auto', ...style }} />
);

function OneDrivePickerModal({ isOpen, onClose, onFilesSelected, botId, remainingSlots = Infinity, onUpgrade, returnTo = 'knowledge' }) {
  const { t } = useTranslation();
  const [connections, setConnections] = useState([]);
  // 'Manage accounts' -> the shared ManageConnectionsModal (rename/delete a
  // connected account). Re-added 2026-08-18: the old Connect page that hosted it
  // is gone, and the picker is where accounts are chosen, so this is where
  // customers look for it.
  const [manageOpen, setManageOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderStack, setFolderStack] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [importing, setImporting] = useState(false);
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolvedFiles, setResolvedFiles] = useState([]);
  const [resolveError, setResolveError] = useState(false);

  const oauthPopupRef = useRef(null);

  const isInIframe = () => {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  };

  // Fetch user's OneDrive connections
  const fetchConnections = useCallback(async () => {
    try {
      const userId = localStorage.getItem('user_id');
      const response = await fetch(`${API_URL}data-connections?user_id=${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const onedriveConnections = (data.connections || []).filter(
          c => c.platform === 'onedrive' && c.status === 'active'
        );
        setConnections(onedriveConnections);

        if (onedriveConnections.length === 1) {
          setSelectedConnection(onedriveConnections[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data connections:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setFiles([]);
      setCurrentFolderId(null);
      setFolderStack([]);
      setSelectedFiles([]);
      fetchConnections();
    }
  }, [isOpen, fetchConnections]);

  // Fetch files when connection is selected
  const fetchFiles = useCallback(async (folderId = null) => {
    if (!selectedConnection) return;

    setLoadingFiles(true);
    try {
      let url = `${API_URL}data-connections/${selectedConnection.id}/onedrive-files`;
      if (folderId) {
        url += `?folder_id=${folderId}`;
      }

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setFiles(data.files || []);
      } else {
        console.error('Failed to fetch files');
        setFiles([]);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      setFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }, [selectedConnection]);

  useEffect(() => {
    if (selectedConnection) {
      fetchFiles(currentFolderId);
    }
  }, [selectedConnection, currentFolderId, fetchFiles]);

  const handleFolderClick = (folder) => {
    setFolderStack([...folderStack, { id: currentFolderId, name: folder.name }]);
    setCurrentFolderId(folder.id);
    setSelectedFiles([]);
  };

  const handleBackClick = () => {
    if (folderStack.length > 0) {
      const newStack = [...folderStack];
      const parent = newStack.pop();
      setFolderStack(newStack);
      setCurrentFolderId(parent.id);
      setSelectedFiles([]);
    }
  };

  const handleFileToggle = (file) => {
    // Folders are selectable too — picking a folder imports everything inside it.
    setSelectedFiles(prev => {
      const isSelected = prev.some(f => f.id === file.id);
      if (isSelected) {
        return prev.filter(f => f.id !== file.id);
      } else {
        return [...prev, file];
      }
    });
  };

  const doImport = async (allFiles) => {
    // Per-file size cap (mirrors backend ingest_limits): refuse before a row is created.
    const { ok: filesToImport, tooLarge } = splitTooLarge(allFiles);
    if (tooLarge.length) alert(describeTooLarge(tooLarge, t));
    if (!filesToImport.length || !selectedConnection) return;
    setImporting(true);
    try {
      const response = await fetch(
        `${API_URL}data-connections/${selectedConnection.id}/onedrive-import-files`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            bot_id: botId,
            files: filesToImport,
          }),
        }
      );

      if (!response.ok) {
        // 403s are handled globally by the permission-denied modal —
        // close this picker quietly instead of showing a generic alert.
        if (response.status === 403) {
          onClose();
          return;
        }
        throw new Error('Failed to import files');
      }

      const result = await response.json();

      if (onFilesSelected) {
        onFilesSelected(result.knowledge_ids, filesToImport);
      }

      onClose();
    } catch (error) {
      console.error('Error importing files:', error);
      alert('Failed to import files. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const importFiles = async () => {
    if (!selectedFiles.length || !selectedConnection) return;

    // If a folder is selected, resolve its full contents on the backend and
    // show a confirmation list (with a plan-limit check) before importing.
    if (selectedFiles.some(f => f.isFolder)) {
      setResolvedFiles([]);
      setResolveError(false);
      setConfirmOpen(true);
      setResolving(true);
      try {
        const files = await resolveSelection(selectedConnection.id, selectedFiles);
        setResolvedFiles(files);
      } catch (error) {
        console.error('Error resolving folder selection:', error);
        setResolvedFiles([]);
        setResolveError(true);
      } finally {
        setResolving(false);
      }
      return;
    }

    // Single files only — enforce the limit and import directly.
    if (selectedFiles.length > remainingSlots) {
      setLimitModalOpen(true);
      return;
    }
    await doImport(selectedFiles);
  };

  const handleConnectOneDrive = async () => {
    try {
      const userId = localStorage.getItem('user_id');
      const response = await fetch(
        `${API_URL}data-connections/onedrive/connect?user_id=${userId}&return_to=${encodeURIComponent(returnTo)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('pendingKnowledgeTab', 'onedrive');
        localStorage.setItem('pendingOpenPicker', 'onedrive');

        if (isInIframe()) {
          const width = 600;
          const height = 700;
          const left = window.screenX + (window.outerWidth - width) / 2;
          const top = window.screenY + (window.outerHeight - height) / 2;

          oauthPopupRef.current = window.open(
            data.authorization_url,
            'onedrive-oauth',
            `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
          );

          const checkPopup = setInterval(() => {
            if (oauthPopupRef.current?.closed) {
              clearInterval(checkPopup);
              oauthPopupRef.current = null;
              fetchConnections();
            }
          }, 1000);
        } else {
          window.location.href = data.authorization_url;
        }
      }
    } catch (error) {
      console.error('Failed to connect OneDrive:', error);
      alert('Failed to connect OneDrive');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="onedrive-modal-overlay" onClick={onClose}>
      <div className="onedrive-modal-content" onClick={e => e.stopPropagation()}>
        <div className="onedrive-modal-header">
          <h2>
            <OneDriveLogo size={24} style={{ marginRight: '10px' }} />
            {t('onedrive_import_title') || 'Import from OneDrive'}
          </h2>
          <button className="onedrive-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="onedrive-modal-body">
          {loading ? (
            <div className="onedrive-loading">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <p>{t('loading')}</p>
            </div>
          ) : connections.length === 0 ? (
            <div className="onedrive-no-connections">
              <OneDriveLogo size={64} style={{ marginBottom: '0px', opacity: 0.5 }} />
              <p>{t('onedrive_no_account_connected') || 'No OneDrive account connected'}</p>
              <p className="onedrive-hint">
                {t('onedrive_connect_hint') || 'Connect your OneDrive account to import documents.'}
              </p>
              <Button
                onClick={handleConnectOneDrive}
                variant="contained"
                sx={{
                  backgroundColor: '#5616ea',
                  '&:hover': { backgroundColor: '#4a14d6' },
                }}
              >
                {t('connect_onedrive') || 'Connect OneDrive'}
              </Button>
            </div>
          ) : (
            <>
              <div className="onedrive-account-selector">
                <FormControl
                  fullWidth
                  sx={{
                    '& label.Mui-focused': { color: '#5616ea' },
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': { border: '1px solid #515151' },
                    },
                  }}
                >
                  <InputLabel id="onedrive-account-select-label">
                    {t('onedrive_select_account') || 'Select OneDrive Account'}
                  </InputLabel>
                  <Select
                    labelId="onedrive-account-select-label"
                    value={selectedConnection?.id || ''}
                    onChange={e => {
                      const conn = connections.find(c => c.id === parseInt(e.target.value));
                      setSelectedConnection(conn);
                      setCurrentFolderId(null);
                      setFolderStack([]);
                      setSelectedFiles([]);
                    }}
                    label={t('onedrive_select_account') || 'Select OneDrive Account'}
                  >
                    {connections.map(conn => (
                      <MenuItem key={conn.id} value={conn.id}>
                        {conn.display_name || conn.external_id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <span className="onedrive-add-account-link" onClick={handleConnectOneDrive}>
                  {t('add_another_account') || 'Add another account'}
                </span>
                {connections.length > 0 && (

                  <span className="onedrive-add-account-link" style={{ marginLeft: '16px' }} onClick={() => setManageOpen(true)}>

                    {t('manage_accounts', 'Manage accounts')}

                  </span>

                )}
              </div>

              {selectedConnection && (
                <div className="onedrive-file-browser">
                  {folderStack.length > 0 && (
                    <button className="onedrive-back-button" onClick={handleBackClick}>
                      <FontAwesomeIcon icon={faArrowLeft} />
                      <span>{t('back')}</span>
                    </button>
                  )}

                  <div className="onedrive-breadcrumb">
                    <span>OneDrive</span>
                    {folderStack.map((folder, idx) => (
                      <span key={idx}> / {folder.name || t('folder_root')}</span>
                    ))}
                  </div>

                  {loadingFiles ? (
                    <div className="onedrive-loading-files">
                      <FontAwesomeIcon icon={faSpinner} spin />
                      <span>{t('loading_files')}</span>
                    </div>
                  ) : files.length === 0 ? (
                    <div className="onedrive-empty-folder">
                      <p>{t('no_supported_files_in_folder')}</p>
                      <p className="onedrive-hint">{t('supported_file_types_hint')}</p>
                    </div>
                  ) : (
                    <div className="onedrive-file-list">
                      {files.map(file => (
                        <div
                          key={file.id}
                          className={`onedrive-file-item ${file.isFolder ? 'folder' : ''} ${
                            selectedFiles.some(f => f.id === file.id) ? 'selected' : ''
                          }`}
                          onClick={() => file.isFolder ? handleFolderClick(file) : handleFileToggle(file)}
                        >
                          <Checkbox
                            checked={selectedFiles.some(f => f.id === file.id)}
                            onChange={() => handleFileToggle(file)}
                            onClick={e => e.stopPropagation()}
                            sx={{
                              color: '#5616ea',
                              '&.Mui-checked': { color: '#5616ea' },
                              padding: '4px',
                            }}
                          />
                          <FontAwesomeIcon
                            icon={file.isFolder ? faFolder : faFile}
                            className={`onedrive-file-icon ${file.isFolder ? 'folder-icon' : 'file-icon'}`}
                          />
                          <span className="onedrive-file-name">{file.name}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedFiles.length > 0 && (
                    <div className="onedrive-selected-count">
                      {t('n_files_selected', { count: selectedFiles.length })}
                    </div>
                  )}
                </div>
              )}

              <div className="onedrive-modal-actions">
                <Button
                  onClick={onClose}
                  variant="outlined"
                  sx={{
                    color: '#5616ea',
                    borderColor: '#5616ea',
                    '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' },
                  }}
                >
                  {t('cancel')}
                </Button>
                <Button
                  onClick={importFiles}
                  variant="contained"
                  disabled={selectedFiles.length === 0 || importing}
                  sx={{
                    backgroundColor: '#5616ea',
                    '&:hover': { backgroundColor: '#4a14d6' },
                    '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' },
                  }}
                >
                  {importing ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />
                      {t('importing') || 'Importing...'}
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faArrowUpFromBracket} style={{ marginRight: '8px' }} />
                      {t('import_selected') || 'Import Selected'}
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <KnowledgeLimitInfoModal
        open={limitModalOpen}
        onClose={() => setLimitModalOpen(false)}
        onUpgrade={() => { setLimitModalOpen(false); onUpgrade && onUpgrade(); }}
        remainingSlots={remainingSlots}
      />

      <ConfirmImportModal
        open={confirmOpen}
        loading={resolving}
        error={resolveError}
        files={resolvedFiles}
        importing={importing}
        remainingSlots={remainingSlots}
        onClose={() => setConfirmOpen(false)}
        onConfirm={(selected) => { setConfirmOpen(false); doImport(selected); }}
        onUpgrade={() => { setConfirmOpen(false); onUpgrade && onUpgrade(); }}
      />
      {/* Portal-rendered, but a React child of the overlay above (onClick=onClose):
          clicks inside the manage/confirm dialogs would bubble to it and close the
          picker before the delete could run — so stop them here. */}
      <div onClick={(e) => e.stopPropagation()}>
        <ManageConnectionsModal
          open={manageOpen}
          onClose={() => setManageOpen(false)}
          connections={connections}
          onUpdate={fetchConnections}
          platform="onedrive"
          connectionType="data"
        />
      </div>
    </div>
  );
}

export default OneDrivePickerModal;
